import { Checkbox, Drawer, Input, message, Select, Space } from 'antd';
import React, { useState } from 'react';
import { Button, Form } from 'antd';
import Title from 'antd/es/typography/Title';
import { DeliveryConfigurationDTO } from '../../../backendapi/requests';
import { useClientsServiceGetBackendClients, useDeliveryConfigurationsServicePostBackendDeliveryConfigurations } from '../../../backendapi/queries';

type DeliveryMethod = 'CustomAPI' | 'FTP' | 'HostedStorage' | 'S3' | 'SFTP';
const DeliveryMethodValues: DeliveryMethod[] = ['CustomAPI', 'FTP', 'HostedStorage', 'S3', 'SFTP'];

interface FormData extends DeliveryConfigurationDTO {
    deliveryMethods: DeliveryMethod[];
}

const CreateDeliveryConfiguration: React.FC<{ isOpen: boolean, onClose: () => void }> = ({ isOpen, onClose }) => {
    const { data: clients } = useClientsServiceGetBackendClients({}, undefined, { enabled: isOpen });
    const { mutateAsync } = useDeliveryConfigurationsServicePostBackendDeliveryConfigurations();

    const [form] = Form.useForm<FormData>();
    const [messageApi, contextHolder] = message.useMessage();
    const deliveryMethods = Form.useWatch('deliveryMethods', form) ?? [];
    const [saving, setSaving] = useState(false);

    const onSubmit = () => {
        form.submit();
    };

    const onFinish = () => {
        setSaving(true);

        const values = form.getFieldsValue(true) as FormData;
        values.deliveryMethods = values.deliveryMethods ?? [];

        if(!values.deliveryMethods.includes("CustomAPI")){
            values.customApi = undefined!;
        }
        if(!values.deliveryMethods.includes("FTP")){
            values.ftp = undefined!;
        }
        if(!values.deliveryMethods.includes("HostedStorage")){
            values.hostedStorage = undefined!;
        }else{
            values.hostedStorage = {};
        }
        if(!values.deliveryMethods.includes("S3")){
            values.s3 = undefined!;
        }
        if(!values.deliveryMethods.includes("SFTP")){
            values.sftp = undefined!;
        }

        mutateAsync({ requestBody: values }).then(() => {
            messageApi.success("Delivery configuration created");
            setSaving(false);
            onClose();
        }).catch(() => {
            messageApi.error("An error occured");
            setSaving(false);
        });
    };

    return (
        <>
            {contextHolder}

            <Drawer
                title="Create a new configuration"
                width={720}
                onClose={onClose}
                open={isOpen}
                bodyStyle={{ paddingBottom: 80 }}
                extra={
                    <Space>
                        <Button onClick={onClose}>Cancel</Button>
                        <Button onClick={onSubmit} type="primary" loading={saving}>
                            Submit
                        </Button>
                    </Space>
                }
            >
                <Form
                    name="basic"
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}>

                    <Form.Item rules={[{ required: true }]} name='clientId' label="Client">
                        <Select options={clients?.value.map(e => ({ label: e.name, value: e.id }))} />
                    </Form.Item>

                    <Form.Item rules={[{ required: true }]} name={'title'} label="Title">
                        <Input />
                    </Form.Item>

                    <Form.Item name="isDefault" valuePropName="checked">
                        <Checkbox>Default</Checkbox>
                    </Form.Item>

                    <Form.Item name="deliveryMethods" label='Method'>

                        <Checkbox.Group options={DeliveryMethodValues} />

                    </Form.Item>

                    {(deliveryMethods.includes("CustomAPI")) && <>
                        <Title level={4}>CustomAPI</Title>
                        <Form.Item rules={[{ required: true }]} name={['customApi', 'endpoint']} label="Endpoint">
                            <Input />
                        </Form.Item>
                    </>}

                    {(deliveryMethods.includes("FTP")) && <>
                        <Title level={4}>FTP</Title>
                        <Form.Item rules={[{ required: true }]} name={['ftp', 'hostname']} label="Hostname">
                            <Input />
                        </Form.Item>
                        <Form.Item rules={[{ required: true }]} name={['ftp', 'port']} label="Port">
                            <Input type='number' />
                        </Form.Item>
                        <Form.Item rules={[{ required: true }]} name={['ftp', 'username']} label="Username">
                            <Input />
                        </Form.Item>
                        <Form.Item rules={[{ required: true }]} name={['ftp', 'password']} label="Password">
                            <Input />
                        </Form.Item>
                        <Form.Item rules={[{ required: true }]} name={['ftp', 'folder']} label="Folder">
                            <Input />
                        </Form.Item>
                    </>}

                    {(deliveryMethods.includes("HostedStorage")) && <>

                    </>}

                    {(deliveryMethods.includes("S3")) && <>
                        <Title level={4}>S3</Title>
                        <Form.Item rules={[{ required: true }]} name={['s3', 'serviceEndpoint']} label="Service endpoint">
                            <Input />
                        </Form.Item>
                        <Form.Item rules={[{ required: true }]} name={['s3', 'accessSecret']} label="Access secret">
                            <Input />
                        </Form.Item>
                        <Form.Item rules={[{ required: true }]} name={['s3', 'accessKey']} label="access key">
                            <Input />
                        </Form.Item>
                        <Form.Item rules={[{ required: true }]} name={['s3', 'bucketName']} label="Bucket name">
                            <Input />
                        </Form.Item>
                        <Form.Item rules={[{ required: true }]} name={['s3', 'pathPrefix']} label="Path prefix">
                            <Input />
                        </Form.Item>
                    </>}

                    {(deliveryMethods.includes("SFTP")) && <>
                        <Title level={4}>SFTP</Title>

                        <Form.Item rules={[{ required: true }]} name={['sftp', 'hostname']} label="Hostname">
                            <Input />
                        </Form.Item>
                        <Form.Item rules={[{ required: true }]} name={['sftp', 'port']} label="Port">
                            <Input type='number' />
                        </Form.Item>
                        <Form.Item rules={[{ required: true }]} name={['sftp', 'username']} label="Username">
                            <Input />
                        </Form.Item>
                        <Form.Item rules={[{ required: true }]} name={['sftp', 'sshkey']} label="Sshkey">
                            <Input />
                        </Form.Item>
                        <Form.Item rules={[{ required: true }]} name={['sftp', 'folder']} label="Folder">
                            <Input />
                        </Form.Item>
                    </>}
                </Form>
            </Drawer>
        </>

    );
};

export default CreateDeliveryConfiguration;